/**
 * Defaults for the Media & Text with Modal Section block.
 */

import {
	FUND_LAYOUT_VARIANTS,
	CAPITAL_MODAL_DEFAULTS,
} from './modal-constants';

export const BLOCK_NAME = 'alteia/media-text-modal-section';

export const WEBSITE_VARIANTS = {
	FUND: 'fund',
	CAPITAL: 'capital',
};

export const BUTTON_ACTIONS = {
	OPEN_MODAL: 'open-modal',
	NAVIGATE: 'navigate',
};

export const MODAL_TYPES = {
	FUND: 'fund',
	CAPITAL: 'capital',
};

export const THEME_COLORS = {
	primary: '#0a4f55',
	primaryGreen: '#6dbe45',
	primaryBlue: '#009bab',
	primaryLight: '#F0F6F5',
	primaryTealLight: '#33817d',
	secondary: '#707070',
	white: '#ffffff',
	capitalGreen: '#38AE54',
	capitalPurple: '#6E339E',
	capitalBlue: '#3170BE',
};

export const IMAGE_BASE =
	'/wp-content/themes/alteia/assets/src/images/strategies-media/';

export const FALLBACK_IMAGE =
	'/wp-content/themes/alteia/assets/src/images/cargo-ships.jpg';

/**
 * Generate a stable unique card ID.
 *
 * @param {string} [prefix] Optional slug prefix.
 * @return {string} Unique card ID.
 */
export function generateCardId( prefix = 'card' ) {
	const random = Math.random().toString( 36 ).slice( 2, 9 );
	return `${ prefix }-${ Date.now().toString( 36 ) }-${ random }`;
}

/**
 * Default modal content scaffold (consumed in phase two).
 *
 * @param {string} modalType Modal type.
 * @return {Object} Modal content object.
 */
export function createDefaultModalContent( modalType ) {
	if ( modalType === MODAL_TYPES.CAPITAL ) {
		return {
			aboutTheFund: '',
			investmentObjective: '',
			fundOverview: [],
			investmentCharacteristics: [],
			fundDocuments: [],
			interestedHeading: 'Interested in this fund?',
			interestedText: '',
			ctaLabel: 'Contact us',
			ctaUrl: '',
			ctaOpensInNewTab: true,
		};
	}

	return {
		overview: '',
		howItWorks: '',
		howItWorksItems: [],
		availableVehicles: '',
		keyCharacteristics: [],
		keyCharacteristicsFootnote: '',
		nextStep: '',
		ctaLabel: 'Get in touch',
		ctaUrl: '',
		ctaOpensInNewTab: true,
	};
}

/**
 * @param {Object} overrides Card overrides.
 * @return {Object} Card defaults.
 */
export function createCard( overrides = {} ) {
	return {
		id: generateCardId(),
		imageId: 0,
		imageUrl: '',
		imageAlt: '',
		swapColumns: false,
		title: '',
		description: '',
		secondaryDescription: '',
		badgeLabel: '',
		badgeEnabled: false,
		accentColor: '',
		enableButton: true,
		buttonLabel: 'Learn more',
		buttonAction: BUTTON_ACTIONS.OPEN_MODAL,
		buttonUrl: '',
		buttonOpensInNewTab: false,
		buttonRel: '',
		modalEnabled: true,
		modalTitle: '',
		modalType: MODAL_TYPES.FUND,
		modalLayoutVariant: 'standard',
		modalContentId: '',
		modalAccentColor: '',
		modalContent: createDefaultModalContent( MODAL_TYPES.FUND ),
		...overrides,
	};
}

export const FUND_MODAL_CONVENTIONAL = {
	overview:
		'Conventional trade finance provides exposure to short-duration transactions linked to the movement of physical goods across Africa and the GCC. Facilities are structured around identifiable trade flows, with repayment linked to the completion of underlying commercial activity.',
	howItWorks:
		'Capital is deployed into carefully selected trade transactions involving commodities and other physical goods.',
	howItWorksItems: [
		'Identifiable underlying assets',
		'First-ranking security',
		'Legal documentation completed before funds are released',
	],
	availableVehicles:
		"Access this strategy through Alteia Fund's conventional trade finance investment vehicles.",
	keyCharacteristics: [
		'Short tenors, typically 90–120 days',
		'Self-liquidating transactions',
		'Defined repayment pathways',
		'USD-denominated',
		'Diversified across sectors, counterparties, and trade corridors',
		'Target returns of 10–12%* per annum',
	],
	keyCharacteristicsFootnote:
		'*Target returns are not guaranteed. Past performance is not indicative of future results.',
	nextStep:
		'Request fund documentation or speak with our team to discuss your investment objectives.',
	ctaLabel: 'Get in touch',
	ctaUrl: '',
	ctaOpensInNewTab: true,
};

export const FUND_MODAL_SHARIAH = {
	overview:
		'Shariah-compliant investment solutions applying the same underwriting standards and risk framework as conventional trade finance, with additional Shariah-specific controls built into every transaction.',
	howItWorks:
		'Transactions are based on the purchase and sale of physical commodities in accordance with Shariah principles.',
	howItWorksItems: [
		'Commodity ownership forms part of the transaction',
		'Capital is deployed only after the underlying commodity is identified and priced',
		'Transactions are reviewed for Shariah compliance',
	],
	keyCharacteristics: [
		'Physical commodity ownership as the primary risk control',
		'Capital deployed only after the underlying commodity is identified, priced, and held',
		'Transactions aligned with applicable Shariah principles',
		'Focus on capital preservation and repayment visibility',
		'Short tenors, typically 90–180 days',
		'Self-liquidating transactions linked to real trade activity',
		'Approved by the Shariah Review Bureau',
		'Target returns of 10%* per annum',
	],
	keyCharacteristicsFootnote:
		'*Target returns are not guaranteed. Past performance is not indicative of future results.',
	availableVehicles:
		'Shariah-compliant investment solutions are available through two entities. Alteia Fund Management manages the Alteia Commodity Trade Fund and structured trade finance funds with Shariah-compliant structures. Shariah-compliant funds in Saudi Arabia are managed through Alteia Capital, our CMA-licensed entity in Saudi Arabia, and are approved by the Shariah Review Bureau.',
	nextStep:
		'To learn more about Shariah-compliant investment solutions, explore Alteia Capital or contact our team directly.',
	ctaLabel: 'Explore Alteia Capital',
	ctaUrl: '',
	ctaOpensInNewTab: true,
};

export const FUND_DEFAULT_CARDS = [
	createCard( {
		id: 'fund-conventional',
		imageUrl: `${ IMAGE_BASE }Conventional.png`,
		imageAlt: 'Colorful shipping containers',
		swapColumns: false,
		title: 'Conventional',
		description:
			'Short-duration financing linked to physical trade flows across Africa and the GCC, secured against underlying assets and available across multiple fund vehicles.',
		accentColor: THEME_COLORS.primary,
		modalEnabled: true,
		modalTitle: 'Conventional',
		modalType: MODAL_TYPES.FUND,
		modalLayoutVariant: FUND_LAYOUT_VARIANTS.FULL_WIDTH_CTA,
		modalContentId: 'conventional',
		modalAccentColor: THEME_COLORS.primary,
		modalContent: FUND_MODAL_CONVENTIONAL,
	} ),
	createCard( {
		id: 'fund-shariah',
		imageUrl: `${ IMAGE_BASE }Shariah.png`,
		imageAlt: 'Ornate mosque interior',
		swapColumns: true,
		title: 'Shariah',
		description:
			'Shariah-compliant investment solutions built on the same underwriting standards and risk framework, with physical commodity ownership as the primary risk control.',
		accentColor: THEME_COLORS.primary,
		modalEnabled: true,
		modalTitle: 'Shariah',
		modalType: MODAL_TYPES.FUND,
		modalLayoutVariant: FUND_LAYOUT_VARIANTS.SPLIT_CTA,
		modalContentId: 'shariah',
		modalAccentColor: THEME_COLORS.primary,
		modalContent: FUND_MODAL_SHARIAH,
	} ),
];

export const CAPITAL_DEFAULT_CARDS = [
	createCard( {
		id: 'capital-saudi-murabaha',
		imageUrl: `${ IMAGE_BASE }Murabaha.png`,
		imageAlt: 'Palm grove landscape',
		swapColumns: false,
		title: 'Alteia Saudi Murabaha fund',
		description:
			'Provides exposure to short-duration Murabaha financing opportunities structured in accordance with Shariah principles, with defined repayment schedules and asset-backed security.',
		secondaryDescription:
			'Best suited for investors seeking predictable returns over shorter investment horizons.',
		badgeLabel: 'Short duration',
		badgeEnabled: true,
		accentColor: THEME_COLORS.capitalGreen,
		modalEnabled: true,
		modalTitle: 'Alteia Saudi Murabaha fund',
		modalType: MODAL_TYPES.CAPITAL,
		modalLayoutVariant: 'standard',
		modalContentId: 'alteia-saudi-murabaha',
		modalAccentColor: THEME_COLORS.capitalGreen,
		modalContent: { ...CAPITAL_MODAL_DEFAULTS[ 'alteia-saudi-murabaha' ] },
	} ),
	createCard( {
		id: 'capital-saudi-musharaka',
		imageUrl: `${ IMAGE_BASE }Musharaka.png`,
		imageAlt: 'Desert rock formations with trees',
		swapColumns: true,
		title: 'Alteia Saudi Musharaka fund',
		description:
			'Provides access to partnership-based commercial opportunities structured in accordance with Shariah principles, with profit and loss shared between partners.',
		secondaryDescription:
			'Best suited for investors comfortable with longer investment horizons and partnership-based returns.',
		badgeLabel: 'Partnership-based',
		badgeEnabled: true,
		accentColor: THEME_COLORS.capitalPurple,
		modalEnabled: true,
		modalTitle: 'Alteia Saudi Musharaka fund',
		modalType: MODAL_TYPES.CAPITAL,
		modalLayoutVariant: 'standard',
		modalContentId: 'alteia-saudi-musharaka',
		modalAccentColor: THEME_COLORS.capitalPurple,
		modalContent: { ...CAPITAL_MODAL_DEFAULTS[ 'alteia-saudi-musharaka' ] },
	} ),
	createCard( {
		id: 'capital-alfowzan-murabaha',
		imageUrl: `${ IMAGE_BASE }Alfowzan-Murabaha.png`,
		imageAlt: 'Modern skyscrapers',
		swapColumns: false,
		title: 'Alfowzan Murabaha fund',
		description:
			'A strategic partnership fund providing access to Murabaha financing opportunities through a collaboration with Alfowzan Holding.',
		secondaryDescription:
			'Best suited for investors seeking exposure to established counterparties through a strategic partnership structure.',
		badgeLabel: 'Strategic partnership',
		badgeEnabled: true,
		accentColor: THEME_COLORS.capitalBlue,
		modalEnabled: true,
		modalTitle: 'Alfowzan Murabaha fund',
		modalType: MODAL_TYPES.CAPITAL,
		modalLayoutVariant: 'standard',
		modalContentId: 'alfowzan-murabaha',
		modalAccentColor: THEME_COLORS.capitalBlue,
		modalContent: { ...CAPITAL_MODAL_DEFAULTS[ 'alfowzan-murabaha' ] },
	} ),
];

export const VARIANT_DEFAULTS = {
	[ WEBSITE_VARIANTS.FUND ]: {
		heading: 'Structures available to investors',
		subtitle:
			'Two structures. One process. Designed to meet different investor requirements.',
		description: '',
		disableSubtitle: false,
		disableDescription: true,
		cards: FUND_DEFAULT_CARDS,
	},
	[ WEBSITE_VARIANTS.CAPITAL ]: {
		heading: 'Explore our funds',
		subtitle: '',
		description:
			'Alteia Capital offers access to a range of Shariah-compliant investment funds, each structured to meet different investor requirements and return profiles.',
		disableSubtitle: true,
		disableDescription: false,
		cards: CAPITAL_DEFAULT_CARDS,
	},
};

export const DEFAULT_SECTION_PADDING = {
	top: '80',
	right: '0',
	bottom: '80',
	left: '0',
};
